import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FaSpinner, FaPlus } from "../utils/icons";
import { authService } from "../services/auth";
import {
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/design-system/components/react";

/** Admin: list of companies → each links to a detail page (cohorts + members). */
export default function CompaniesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    data: companies,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["admin-companies"],
    queryFn: () => authService.getCompanies(),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (vars: { name: string; code: string }) =>
      authService.createCompany(vars.name, vars.code),
    onSuccess: (company) => {
      queryClient.invalidateQueries({ queryKey: ["admin-companies"] });
      setShowCreate(false);
      setName("");
      setCode("");
      navigate(`/admin/companies/${encodeURIComponent(company.id)}`);
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setCreateError(detail || "Could not create company");
    },
  });

  if (isLoading) {
    return (
      <div className="p-8 text-fg-muted flex items-center gap-2">
        <FaSpinner className="animate-spin" /> Loading companies…
      </div>
    );
  }
  if (isError) {
    return <div className="p-8 text-warn">Failed to load companies.</div>;
  }

  const rows = companies ?? [];

  return (
    <div className="max-w-4xl mx-auto p-6 text-fg">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold">Companies</h1>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-company">
          <FaPlus className="w-3 h-3 mr-1.5" /> Create company
        </Button>
      </div>
      <p className="text-sm text-fg-muted mb-5">
        Assign a cohort to a company and every member inherits access. Members can still be granted
        or blocked individually.
      </p>

      {rows.length === 0 ? (
        <div className="text-fg-muted text-sm border border-border rounded-frame p-6">
          No companies yet — create one to get started.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {rows.map((c) => (
            <Link
              key={c.id}
              to={`/admin/companies/${encodeURIComponent(c.id)}`}
              className="block bg-surface-2 border border-border rounded-frame p-4 hover:border-signal-stroke-soft hover:bg-signal-ambient transition-colors"
            >
              <div className="font-semibold">{c.display_name}</div>
              <div className="text-xs text-fg-muted font-mono mt-0.5">{c.id}</div>
              <div className="text-sm text-fg-muted mt-2">
                {c.member_count} {c.member_count === 1 ? "member" : "members"} · {c.cohort_count}{" "}
                {c.cohort_count === 1 ? "cohort" : "cohorts"}
              </div>
            </Link>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create company</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="company-name">Company name</Label>
            <Input
              id="company-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setCreateError(null);
              }}
              placeholder="Acme Radiology"
              data-testid="company-name-input"
            />
            <Label htmlFor="company-code">Customer code</Label>
            <Input
              id="company-code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toLowerCase());
                setCreateError(null);
              }}
              placeholder="rdn"
              data-testid="company-code-input"
            />
            <p className="text-[11px] text-fg-muted">
              2–8 lowercase letters/digits (e.g. <code>rdn</code>, <code>htm</code>). The company
              can only be assigned cohorts whose id starts with this code.
            </p>
            {createError && <p className="text-xs text-warn">{createError}</p>}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createMut.mutate({ name: name.trim(), code: code.trim() })}
              disabled={!name.trim() || !/^[a-z0-9]{2,8}$/.test(code.trim()) || createMut.isPending}
              data-testid="company-create-submit"
            >
              {createMut.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
